import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    fetchBidderPreferencesIfNeeded,
    updateBidderPreferences,
} from '@/redux/modules/bidder/preferences/bidderPreferences.actions';
import {
    bidderPreferenceLoadingSelector,
    bidderPreferenceSelector,
} from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import { setPreferredCurrencyCookie } from '@/redux/modules/cookies';

type Props = {
    className?: string;
    onSaveCallback?: () => void;
};

export const CurrencySelection = ({ className, onSaveCallback }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const bidderPreferences = useAppSelector(bidderPreferenceSelector);
    const bidderPreferenceLoading = useAppSelector(bidderPreferenceLoadingSelector);
    const [selectedCurrency, setSelectedCurrency] = useState(bidderPreferences?.preferredCurrency);

    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedCurrency(event.currentTarget.value);
    }, []);

    const onSave = useCallback(() => {
        if (!bidderPreferences) {
            return;
        }
        dispatch(
            updateBidderPreferences({
                ...bidderPreferences,
                preferredCurrency: selectedCurrency,
            })
        );
        onSaveCallback?.();
        dispatch(setPreferredCurrencyCookie());
    }, [dispatch, bidderPreferences, onSaveCallback, selectedCurrency]);

    useEffect(() => {
        setSelectedCurrency(bidderPreferences?.preferredCurrency);
    }, [bidderPreferences]);

    useEffect(() => {
        dispatch(fetchBidderPreferencesIfNeeded({}));
    });

    const radioClassName = 'mt-xs pb-xs';

    return (
        <section className={cn('flex max-w-[250px] flex-col gap-xs', className)}>
            <Radio.Option
                checked={selectedCurrency === 'GBP'}
                className={radioClassName}
                data-testid="GBP"
                name="currency"
                onChange={onChange}
                value="GBP"
            >
                GBP
            </Radio.Option>
            <Radio.Option
                checked={selectedCurrency === 'EUR'}
                className={radioClassName}
                data-testid="EUR"
                name="currency"
                onChange={onChange}
                value="EUR"
            >
                EURO
            </Radio.Option>
            <Radio.Option
                checked={selectedCurrency === 'USD'}
                className={radioClassName}
                data-testid="USD"
                name="currency"
                onChange={onChange}
                value="USD"
            >
                USD
            </Radio.Option>
            <Radio.Option
                checked={selectedCurrency === 'N/A'}
                className={radioClassName}
                name="currency"
                onChange={onChange}
                value="N/A"
            >
                <FormattedMessage id="preferredCurrencyConversion.noConversion" />
            </Radio.Option>
            <Button
                className="mt-md w-full"
                data-testid="savePreferancesButton"
                loading={bidderPreferenceLoading}
                onClick={onSave}
            >
                {formatMessage({ id: 'preferredCurrencyConversion.save' })}
            </Button>
        </section>
    );
};
